/**
 * Wizard step `engagement` (§16.1, §12.1 platforms with the client's account links and order, §12.4 / D07 tasks
 * with bonus values, caps and consent text). Edits the `engagement` slice of the draft through react-hook-form;
 * autosave and validation belong to the frame.
 */
import type { EngagementTaskType, SocialPlatform } from '@windrop/contracts';
import { Button, useIntl } from '@windrop/ui';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import {
  FormErrorSummary,
  RhfCheckbox,
  RhfSelect,
  RhfText,
  RhfTextarea,
} from '../../../components/FormField';
import { useWizard } from './WizardFrame';
import { stepForPath, type DraftConfigInput } from './steps';

const PLATFORMS: SocialPlatform[] = ['WHATSAPP', 'INSTAGRAM', 'FACEBOOK', 'TIKTOK', 'X', 'TELEGRAM', 'OTHER'];
const TASK_TYPES: { value: EngagementTaskType; label: string; defaultBonus: number; rule: string }[] = [
  {
    value: 'REFERRAL',
    label: 'Qualified referral',
    defaultBonus: 2,
    rule: 'Earned when a unique referred participant completes a qualifying entry; self-referrals excluded.',
  },
  {
    value: 'SHARE',
    label: 'Converted share',
    defaultBonus: 1,
    rule: 'Earned once per referred participant who completes an entry — never for a click.',
  },
  {
    value: 'FOLLOW',
    label: 'Follow / join',
    defaultBonus: 1,
    rule: 'Participant attestation plus risk-based spot check; once per platform per lottery.',
  },
  {
    value: 'LINK_CHANNEL',
    label: 'Link another channel',
    defaultBonus: 1,
    rule: 'Second channel handle recorded on the profile; once per platform.',
  },
  {
    value: 'DAILY_STREAK',
    label: 'Daily streak (optional)',
    defaultBonus: 1,
    rule: 'A new qualifying entry on the next day — not merely opening the page.',
  },
];
const ULID_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const newRefId = (): string => {
  const bytes = new Uint8Array(26);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => ULID_ALPHABET[b % 32])
    .join('')
    .replace(/^./, '0');
};

export function EngagementStep() {
  const { control } = useFormContext<DraftConfigInput>();
  const { issues, goToStep } = useWizard();
  const { formatNumber } = useIntl();
  const platforms = useFieldArray({ control, name: 'engagement.platforms' });
  const tasks = useFieldArray({ control, name: 'engagement.tasks' });
  const values = useWatch({ control, name: 'engagement' });
  const mine = issues.filter((i) => i.step === 'engagement');
  const totalBonus = (values?.tasks ?? []).reduce(
    (a, t) => a + (t?.bonusEntries ?? TASK_TYPES.find((x) => x.value === t?.type)?.defaultBonus ?? 0),
    0,
  );

  return (
    <div className="wd-stack" data-testid="engagement-step">
      <FormErrorSummary
        issues={mine}
        title={`${mine.length} issue${mine.length === 1 ? '' : 's'} in engagement`}
        onNavigate={(p) => goToStep(stepForPath(p))}
      />

      <h2>Platforms (D06, §12.1)</h2>
      <p className="wd-muted">
        The client&apos;s public account link per platform, in display order. Winners who arrived from a
        platform default to it; direct arrivals choose one at claim submission.
      </p>
      {platforms.fields.map((f, i) => (
        <fieldset
          key={f.id}
          className="wdc-form-grid wd-surface"
          style={{ padding: 'var(--wd-space-3)' }}
          data-testid={`platform-${i}`}
        >
          <RhfSelect
            control={control}
            name={`engagement.platforms.${i}.platform`}
            label="Platform"
            required
            options={PLATFORMS.map((p) => ({ value: p, label: p }))}
          />
          <RhfText
            control={control}
            name={`engagement.platforms.${i}.accountUrl`}
            label="Account link"
            required
            type="url"
            hint="https://wa.me/…, instagram.com/…, t.me/…"
          />
          <RhfText
            control={control}
            name={`engagement.platforms.${i}.label`}
            label="Label"
            hint="Shown to participants, e.g. @aurora.mobile"
          />
          <RhfText
            control={control}
            name={`engagement.platforms.${i}.displayOrder`}
            label="Order"
            type="number"
            parse={(raw) => Number(raw)}
            format={(v) => String(v ?? 0)}
          />
          <Button variant="quiet" onClick={() => platforms.remove(i)}>
            Remove platform
          </Button>
        </fieldset>
      ))}
      <Button
        variant="secondary"
        onClick={() =>
          platforms.append({
            platform: 'WHATSAPP',
            accountUrl: 'https://wa.me/',
            label: '',
            displayOrder: platforms.fields.length,
          })
        }
        data-testid="add-platform"
      >
        Add platform
      </Button>

      <h2>Engagement tasks (§12.4, D07)</h2>
      <p className="wd-muted">
        Bonus entries carry the same draw weight as ordinary entries and are capped at ten per participant per
        lottery. Progress shown to participants is always real counts. Configured task bonuses add up to{' '}
        {formatNumber(totalBonus)}; the cap below still applies.
      </p>
      <div style={{ maxWidth: 260 }}>
        <RhfText
          control={control}
          name="engagement.totalBonusCap"
          label="Bonus-entry cap per participant"
          required
          type="number"
          hint="D07: at most ten."
          parse={(raw) => Number(raw)}
          format={(v) => String(v ?? 10)}
        />
      </div>
      {tasks.fields.map((f, i) => {
        const type = values?.tasks?.[i]?.type as EngagementTaskType | undefined;
        const def = TASK_TYPES.find((t) => t.value === type);
        return (
          <fieldset
            key={f.id}
            className="wdc-form-grid wd-surface"
            style={{ padding: 'var(--wd-space-3)' }}
            data-testid={`task-${i}`}
          >
            <RhfSelect
              control={control}
              name={`engagement.tasks.${i}.type`}
              label="Task"
              required
              options={TASK_TYPES.map((t) => ({ value: t.value, label: t.label }))}
            />
            <RhfText control={control} name={`engagement.tasks.${i}.title.en`} label="Title (en)" required />
            <RhfText
              control={control}
              name={`engagement.tasks.${i}.bonusEntries`}
              label="Bonus entries"
              hint={def ? `Default +${def.defaultBonus}. ${def.rule}` : 'Default per §12.4 when empty.'}
              type="number"
              parse={(raw) => (raw === '' ? undefined : Number(raw))}
              format={(v) => (v == null ? '' : String(v))}
            />
            <RhfText
              control={control}
              name={`engagement.tasks.${i}.capPerParticipant`}
              label="Cap per participant"
              hint="Follow / link channel default to once per platform."
              type="number"
              parse={(raw) => (raw === '' ? undefined : Number(raw))}
              format={(v) => (v == null ? '' : String(v))}
            />
            {type === 'FOLLOW' || type === 'LINK_CHANNEL' ? (
              <RhfSelect
                control={control}
                name={`engagement.tasks.${i}.platform`}
                label="Platform"
                options={PLATFORMS.map((p) => ({ value: p, label: p }))}
              />
            ) : null}
            {type === 'FOLLOW' ? (
              <RhfText
                control={control}
                name={`engagement.tasks.${i}.destinationUrl`}
                label="Destination page / channel / group"
                type="url"
              />
            ) : null}
            {type === 'DAILY_STREAK' ? (
              <RhfText
                control={control}
                name={`engagement.tasks.${i}.dailyCap`}
                label="Daily cap"
                type="number"
                parse={(raw) => (raw === '' ? undefined : Number(raw))}
                format={(v) => (v == null ? '' : String(v))}
              />
            ) : null}
            <RhfTextarea
              control={control}
              name={`engagement.tasks.${i}.consentText.en`}
              label="Consent text (en)"
              required
              rows={2}
              placeholder="What the participant agrees to when completing this task."
            />
            <RhfCheckbox control={control} name={`engagement.tasks.${i}.enabled`} label="Enabled" />
            <Button variant="quiet" onClick={() => tasks.remove(i)}>
              Remove task
            </Button>
          </fieldset>
        );
      })}
      <Button
        variant="secondary"
        onClick={() =>
          tasks.append({
            taskId: newRefId(),
            type: 'REFERRAL',
            enabled: true,
            title: { en: 'Invite a friend' },
            consentText: { en: 'Your friend will see your display name.' },
          })
        }
        data-testid="add-task"
      >
        Add task
      </Button>
    </div>
  );
}
